import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import styled from 'styled-components';
import { placeholder } from 'polished';

import LoadingSpinner from '@grnhse/seedling/lib/azalea/components/loading_spinner';
import {
  padding,
  fontWeight,
  colors,
} from '@grnhse/seedling/lib/azalea/constants';
import searchIcon from 'shared/common_images/search.svg';
import Button from '@grnhse/seedling/lib/azalea/components/button';
import { XIcon } from '@grnhse/seedling/lib/azalea/icons';
import t from 'shared/utils/translation';

const InputElem = styled.input`
  line-height: 1.5;
  font-size: 14px !important;
  color: ${colors.darkestGrey} !important;
  border: 1px solid ${colors.lightGrey} !important;
  box-sizing: border-box;
  border-radius: 2px;
  width: ${(props) => props.width || 'inherit'} !important;

  // alternative to doing "!important" to everything
  &&& {
    font-size: 14px;
    padding: ${padding.tiny} ${padding.small};
    color: ${colors.darkestGrey};
    border: 1px solid ${colors.lightGrey};
  }

  &:focus {
    border-color: ${colors.mediumGreenhouseGreen} !important;
  }

  ${placeholder({
    font: `${fontWeight.light} 14px 'Untitled Sans', sans-serif`,
    color: `${colors.mediumGrey}`,
    'line-height': '1.5',
  })};
`;

// !important is necessary to override styling in application.css
const BlockInputElem = styled(InputElem)`
  display: block !important;
  width: 100% !important;
`;

/**
 * A very simple abstraction for `<input>` element that adds enable/disable logic
 * with some basic styling so all input fields are the same
 * */
export default class Input extends React.Component {
  static propTypes = {
    disabled: PropTypes.bool,
    block: PropTypes.bool,
    inputRef: PropTypes.func,
    type: PropTypes.string,
    autoFocus: PropTypes.bool,
    selectOnFocus: PropTypes.bool,
  };

  static defaultProps = {
    disabled: false,
    block: false,
    inputRef: () => {},
    type: 'text',
    autoFocus: false,
    selectOnFocus: false,
  };

  onFocus = (e) => {
    if (this.props.selectOnFocus) {
      e.target.select();
    }
  };

  render() {
    const { autoFocus, disabled, block, className, inputRef, ...otherProps } =
      this.props;
    const ComponentType = block ? BlockInputElem : InputElem;

    return (
      <ComponentType
        className={classNames(className, { disabled: disabled })}
        ref={inputRef}
        autoFocus={autoFocus}
        onFocus={this.onFocus}
        disabled={disabled}
        {...otherProps}
      />
    );
  }
}

export class SearchInput extends React.Component {
  render() {
    const { loading, ...otherProps } = this.props;

    return (
      <SearchInputContainer
        className="search-input-container"
        style={{ position: 'relative' }}
      >
        <InputWithSearchIcon {...otherProps} />
        {loading && <LoadingSpinnerIcon size="small" />}
      </SearchInputContainer>
    );
  }
}

export class ExpandableSearchInput extends React.PureComponent {
  static defaultProps = {
    buttonSize: '25px',
  };

  constructor(props) {
    super(props);

    this.state = {
      expanded: false,
    };
  }

  onSearchInputKeyPress = (e) => {
    const ENTER_KEY_CODE = 13;
    const { submitSearch } = this.props;

    if (submitSearch && (e.key === 'Enter' || e.which === ENTER_KEY_CODE)) {
      submitSearch();
    }
  };

  render() {
    const { onClear, submitSearch, buttonSize, loading, ...otherProps } =
      this.props;
    const { expanded } = this.state;

    if (expanded) {
      return (
        <div>
          <SearchInputContainerWithButton>
            <InputWithSearchIcon
              {...otherProps}
              onKeyPress={this.onSearchInputKeyPress}
              clearable={!!onClear}
            />
            {loading && <LoadingSpinnerIcon size="small" />}
            {this.props.value.length > 0 && onClear && (
              <ClearIcon onClick={onClear} />
            )}
          </SearchInputContainerWithButton>

          {submitSearch && (
            <SearchButton fancy onClick={submitSearch}>
              {t('forms.search')}
            </SearchButton>
          )}
        </div>
      );
    }

    return (
      <SearchExpandIcon
        buttonSize={buttonSize}
        onClick={() => this.setState({ expanded: true })}
      />
    );
  }
}

const ClearIcon = styled(XIcon)`
  position: absolute;
  right: 10px;
  top: 25%;
`;

const LoadingSpinnerIcon = styled(LoadingSpinner)`
  position: absolute;
  top: 6px;
  right: 10px;
`;

const SearchButton = styled(Button)`
  display: inline-block;
  margin-left: 5px;
  height: 37px;
  vertical-align: top;
`;

const SearchExpandIcon = styled.div`
  background-image: url('${searchIcon}');
  background-size: ${(props) => props.buttonSize} ${(props) => props.buttonSize};
  background-repeat: no-repeat;
  background-position: 5px 4px;
  width: ${(props) => props.buttonSize};
  height: ${(props) => props.buttonSize};
  cursor: pointer;
`;

const SearchInputContainer = styled.div`
  position: relative;
`;

const SearchInputContainerWithButton = styled(SearchInputContainer)`
  display: inline-block;
`;

const InputWithSearchIcon = styled(Input)`
  // &&&& is a way to create high specificity to overcome any other styling.
  // The problem originates from some !important's in application.scss.
  // Each new component that extends the styling needs to become more
  // and more specific. &&&& will end up looking like .abc.abc.abc.abc,
  // where "abc" is the class auto-generated by styled-components.

  &&&& {
    padding-left: 30px;
    background-image: url('${searchIcon}');
    background-size: 25px 25px;
    background-repeat: no-repeat;
    background-position: 5px 4px;
    ${(props) => (props.clearable ? 'padding-right: 35px;' : '')}
  }
`;
